/**
 * The recruiting ladder and the two stage sentinels, shared by every surface
 * that draws a candidacy stage: the Search Detail charts, the Candidates tab,
 * the New Import wizard's Thrive path and the People List's Thrive columns.
 * Moved here from `routes/client-engagement/searches/charts/seriesDefaults.ts`
 * (epic 2026-10-15-thrive-search-import, sub-task 03) so no feature imports
 * another to read them.
 */

/**
 * Our recruiting ladder, in progression order.
 *
 * A MIRROR of the backend's `FUNNEL_STAGE_ORDER`, kept in sync by hand because
 * the wire carries no ladder: `highest_stage_funnel` ships only the rungs a
 * given search actually reached, and it is empty for a search closed out without
 * progress — so a frontend that derived the ladder from a response would hide
 * every series on exactly the searches with the least data. If the backend's
 * order ever changes, this array changes with it; a test pins the eight names.
 */
export const LADDER_STAGES: readonly string[] = [
  "Source",
  "Identified",
  "Pursuing",
  "Initial Screen",
  "NU Interview",
  "Client Interview",
  "Offer",
  "Placed",
] as const;

const LADDER: ReadonlySet<string> = new Set(LADDER_STAGES);

/**
 * The off-ladder stages a search's candidacies can hold, by name, in
 * `stage_sort_key` order (A-Z, `Rejected` last). A MIRROR of the backend's
 * `OFF_LADDER_STAGES` (`nu_assist_shared/domain/search_report.py`): every
 * Thrive `JobSearchStage` off the ladder plus `Backburner` — the nine names
 * `chartPalette.ts` draws (five on a hue, four neutral). Offered by Combine &
 * Filter's Candidacy stage filter after the ladder (Jose 2026-10-20: a Closed
 * search is mostly Rejected); its test pins it to the API's accepted labels.
 */
export const OFF_LADDER_STAGES: readonly string[] = [
  "Backburner",
  "Carve Out",
  "Client Contact",
  "Jury Member",
  "On Hold",
  "Reference",
  "Stakeholder",
  "Unresponsive",
  "Rejected",
] as const;

/**
 * The two stage sentinels the backend names (`UNKNOWN_STAGE_LABEL`,
 * `REJECTED_STAGE_LABEL` in `nu_assist_shared/domain/search_report.py`), spelled
 * once on the web side so the Candidates tab's chips, pills and the table's
 * order mirror one pair rather than three literals (PR #1009 iter 2).
 * `Unknown` is what a missing stage reads as everywhere; `Rejected` is the one
 * off-ladder stage with a fixed place — last of its group.
 */
export const UNKNOWN_STAGE = "Unknown";
export const REJECTED_STAGE = "Rejected";

/** True for one of the eight rungs — ladder membership, nothing more. */
export function isLadderStage(stage: string): boolean {
  return LADDER.has(stage);
}
